import React, { createContext, useState, useContext, useEffect } from 'react';

const ColumnsContext = createContext();

// Configuración por defecto de columnas visibles (todas menos 'abogado')
const DEFAULT_VISIBLE_COLUMNS = {
  selector: true,
  status: true,
  reference: true,   // User Reference
  creationDate: true, // Fecha de creación
  sendDate: true,
  expirationDate: true,
  client: true,
  counterparty: true,
  lawyer: false  // Por defecto oculta
};

export const useColumnsContext = () => {
  const context = useContext(ColumnsContext);
  if (!context) {
    throw new Error("useColumnsContext must be used within a ColumnsProvider");
  }
  return context;
};

export const ColumnsProvider = ({ children }) => {
  const [visibleColumns, setVisibleColumns] = useState(DEFAULT_VISIBLE_COLUMNS);

  // Cargar configuración desde sessionStorage al inicializar
  useEffect(() => {
    const savedColumns = sessionStorage.getItem('visibleColumns');
    if (savedColumns) {
      try {
        const parsedColumns = JSON.parse(savedColumns);
        // Asegurar que todas las columnas requeridas estén presentes
        const mergedColumns = {
          ...DEFAULT_VISIBLE_COLUMNS,
          ...parsedColumns
        };
        setVisibleColumns(mergedColumns);
      } catch (error) {
        console.error('Error al cargar configuración de columnas:', error);
        // Si hay error, usar configuración por defecto
        setVisibleColumns(DEFAULT_VISIBLE_COLUMNS);
      }
    }
  }, []);

  // Función para actualizar columnas visibles
  const updateVisibleColumns = (newColumns) => {
    setVisibleColumns(newColumns);
    // Guardar en sessionStorage
    sessionStorage.setItem('visibleColumns', JSON.stringify(newColumns));
  };

  // Función para alternar visibilidad de una columna específica
  const toggleColumnVisibility = (columnKey) => {
    // Las columnas reference y creationDate deben permanecer siempre visibles
    if (['reference', 'creationDate'].includes(columnKey)) {
      return; // No permitir cambiar estas columnas
    }
    
    const updatedColumns = {
      ...visibleColumns,
      [columnKey]: !visibleColumns[columnKey]
    };
    updateVisibleColumns(updatedColumns);
  };

  // Función para resetear a configuración por defecto
  const resetToDefault = () => {
    updateVisibleColumns(DEFAULT_VISIBLE_COLUMNS);
  };

  // Función para limpiar configuración (al cerrar sesión)
  const clearConfiguration = () => {
    sessionStorage.removeItem('visibleColumns');
    setVisibleColumns(DEFAULT_VISIBLE_COLUMNS);
  };

  return (
    <ColumnsContext.Provider value={{
      visibleColumns,
      updateVisibleColumns,
      toggleColumnVisibility,
      resetToDefault,
      clearConfiguration
    }}>
      {children}
    </ColumnsContext.Provider>
  );
};

export default ColumnsContext; 